import React, { useMemo } from 'react';
import { BarChart3, Hash, Activity, TrendingUp } from 'lucide-react';
import type { ChartDataPoint, AggregateSummary } from '@/services/PagesServices/CustomReportBuilderService';
import { CHART_COLORS } from '../constants';
import type { ChartType } from '../constants';

interface ChartViewProps {
  chartType: ChartType;
  chartData: ChartDataPoint[];
  chartSummary: AggregateSummary | null;
  chartGroupByLabel: string;
  chartAggregateLabel: string;
  isLoading: boolean;
}

const ChartView: React.FC<ChartViewProps> = ({
  chartType,
  chartData,
  chartSummary,
  chartGroupByLabel,
  chartAggregateLabel,
  isLoading,
}) => {
  const maxChartValue = useMemo(() => {
    if (chartData.length === 0) return 1;
    return Math.max(...chartData.map(d => d.value)) || 1;
  }, [chartData]);

  const pieTotal = useMemo(() => {
    return chartData.reduce((s, d) => s + d.value, 0) || 1;
  }, [chartData]);

  if (isLoading) {
    return (
      <div className="crb-loading">
        <div className="crb-spinner" />
        <p>Generating visualization...</p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="crb-empty-state">
        <div className="crb-empty-icon"><BarChart3 size={36} /></div>
        <h3>Visualize Your Data</h3>
        <p>
          Open the "Visualization" section in the left panel, select a chart type,
          choose a field to group by, and click "Generate Chart" to see your data visualized.
        </p>
      </div>
    );
  }

  return (
    <div className="crb-charts-container">
      {/* Summary Cards */}
      {chartSummary && (
        <SummaryCards summary={chartSummary} aggregateLabel={chartAggregateLabel} />
      )}

      <div className="crb-charts-grid">
        {chartType === 'bar' && (
          <BarChart
            data={chartData}
            maxValue={maxChartValue}
            groupByLabel={chartGroupByLabel}
            aggregateLabel={chartAggregateLabel}
          />
        )}
        {chartType === 'horizontal' && (
          <HorizontalBarChart
            data={chartData}
            maxValue={maxChartValue}
            groupByLabel={chartGroupByLabel}
            aggregateLabel={chartAggregateLabel}
          />
        )}
        {chartType === 'pie' && (
          <PieChartComponent
            data={chartData}
            total={pieTotal}
            groupByLabel={chartGroupByLabel}
            aggregateLabel={chartAggregateLabel}
          />
        )}
      </div>
    </div>
  );
};

// --- Sub-components ---

const SummaryCards: React.FC<{ summary: AggregateSummary; aggregateLabel: string }> = ({ summary, aggregateLabel }) => (
  <div className="crb-summary-cards">
    <SummaryCard icon={<Hash size={20} />} bg="#dbeafe" color="#3b82f6" value={summary.totalRecords} label="Total Records" />
    <SummaryCard icon={<Activity size={20} />} bg="#d1fae5" color="#059669" value={summary.groupCount} label="Groups" />
    <SummaryCard icon={<TrendingUp size={20} />} bg="#fef3c7" color="#d97706" value={summary.sum} label={`Total ${aggregateLabel}`} />
    <SummaryCard icon={<BarChart3 size={20} />} bg="#ede9fe" color="#7c3aed" value={summary.average} label="Average" />
  </div>
);

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  bg: string;
  color: string;
  value: number;
  label: string;
}> = ({ icon, bg, color, value, label }) => (
  <div className="crb-summary-card">
    <div className="crb-summary-icon" style={{ background: bg, color }}>{icon}</div>
    <div className="crb-summary-info">
      <span className="crb-summary-value">{value.toLocaleString()}</span>
      <span className="crb-summary-label">{label}</span>
    </div>
  </div>
);

const BarChart: React.FC<{
  data: ChartDataPoint[];
  maxValue: number;
  groupByLabel: string;
  aggregateLabel: string;
}> = ({ data, maxValue, groupByLabel, aggregateLabel }) => {
  const svgWidth = Math.max(data.length * 60 + 40, 400);

  return (
    <div className="crb-chart-card">
      <div className="crb-chart-card-header">
        <h4>{aggregateLabel} by {groupByLabel}</h4>
        <span className="crb-chart-type-label">Bar Chart</span>
      </div>
      <div className="crb-bar-chart">
        <svg viewBox={`0 0 ${svgWidth} 260`} className="crb-bar-svg">
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = 220 - (ratio * 200);
            return (
              <g key={ratio}>
                <line x1="40" y1={y} x2={svgWidth} y2={y} stroke="var(--color-border)" strokeWidth="1" />
                <text x="35" y={y + 4} fontSize="10" fill="var(--color-secondary-text)" textAnchor="end">
                  {Math.round(maxValue * ratio).toLocaleString()}
                </text>
              </g>
            );
          })}
          {data.map((d, i) => {
            const barHeight = (d.value / maxValue) * 200;
            const x = 50 + i * 60;
            return (
              <g key={i}>
                <rect x={x} y={220 - barHeight} width={40} height={barHeight} rx={4} fill={CHART_COLORS[i % CHART_COLORS.length]} opacity={0.85}>
                  <title>{`${d.label}: ${d.value.toLocaleString()}`}</title>
                </rect>
                <text x={x + 20} y={215 - barHeight} fontSize="10" fill="var(--color-black-text)" textAnchor="middle" fontWeight="600">
                  {d.value.toLocaleString()}
                </text>
                <text
                  x={x + 20} y={240} fontSize="10" fill="var(--color-secondary-text)" textAnchor="middle"
                  transform={data.length > 6 ? `rotate(-30, ${x + 20}, 240)` : ''}
                >
                  {d.label.length > 10 ? d.label.substring(0, 10) + '...' : d.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const HorizontalBarChart: React.FC<{
  data: ChartDataPoint[];
  maxValue: number;
  groupByLabel: string;
  aggregateLabel: string;
}> = ({ data, maxValue, groupByLabel, aggregateLabel }) => (
  <div className="crb-chart-card">
    <div className="crb-chart-card-header">
      <h4>{aggregateLabel} by {groupByLabel}</h4>
      <span className="crb-chart-type-label">Horizontal Bar</span>
    </div>
    <div className="crb-hbar-chart">
      {data.map((d, i) => {
        const widthPct = (d.value / maxValue) * 100;
        return (
          <div key={i} className="crb-hbar-row">
            <div className="crb-hbar-label" title={d.label}>
              {d.label.length > 18 ? d.label.substring(0, 18) + '...' : d.label}
            </div>
            <div className="crb-hbar-track">
              <div
                className="crb-hbar-fill"
                style={{ width: `${widthPct}%`, background: CHART_COLORS[i % CHART_COLORS.length] }}
              />
            </div>
            <div className="crb-hbar-value">{d.value.toLocaleString()}</div>
          </div>
        );
      })}
    </div>
  </div>
);

const PieChartComponent: React.FC<{
  data: ChartDataPoint[];
  total: number;
  groupByLabel: string;
  aggregateLabel: string;
}> = ({ data, total, groupByLabel, aggregateLabel }) => {
  const cx = 110, cy = 110, radius = 90;

  return (
    <div className="crb-chart-card">
      <div className="crb-chart-card-header">
        <h4>{aggregateLabel} by {groupByLabel}</h4>
        <span className="crb-chart-type-label">Pie Chart</span>
      </div>
      <div className="crb-pie-chart-wrapper">
        <svg viewBox="0 0 220 220" className="crb-pie-svg">
          {(() => {
            let currentAngle = -90;
            return data.map((d, i) => {
              const pct = (d.value / total) * 100;
              const angle = (pct / 100) * 360;
              const startRad = (currentAngle * Math.PI) / 180;
              const endRad = ((currentAngle + angle) * Math.PI) / 180;
              const x1 = cx + radius * Math.cos(startRad);
              const y1 = cy + radius * Math.sin(startRad);
              const x2 = cx + radius * Math.cos(endRad);
              const y2 = cy + radius * Math.sin(endRad);
              const largeArc = angle > 180 ? 1 : 0;
              const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
              const midAngle = currentAngle + angle / 2;
              const labelR = radius * 0.6;
              const lx = cx + labelR * Math.cos((midAngle * Math.PI) / 180);
              const ly = cy + labelR * Math.sin((midAngle * Math.PI) / 180);
              currentAngle += angle;
              return (
                <g key={i}>
                  <path d={path} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="#fff" strokeWidth="2">
                    <title>{`${d.label}: ${d.value.toLocaleString()} (${pct.toFixed(1)}%)`}</title>
                  </path>
                  {pct >= 5 && (
                    <text x={lx} y={ly + 4} fontSize="11" fill="#fff" fontWeight="600" textAnchor="middle">
                      {pct.toFixed(0)}%
                    </text>
                  )}
                </g>
              );
            });
          })()}
        </svg>
        <div className="crb-pie-legend">
          {data.map((d, i) => (
            <div key={i} className="crb-pie-legend-item">
              <div className="crb-pie-legend-dot" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
              <span className="crb-pie-legend-text">{d.label}</span>
              <span className="crb-pie-legend-val">{d.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ChartView);
